
import React, { useState } from 'react';
import { PRICING_PLANS } from '../constants';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { ListingTier } from '../types';

const PricingPage: React.FC = () => {
  const [loadingTier, setLoadingTier] = useState<ListingTier | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async (plan: typeof PRICING_PLANS[0]) => {
    setError(null);
    if (plan.tier === ListingTier.FREE) {
       window.location.hash = '/listing-setup?tier=FREE';
       return;
    }

    setLoadingTier(plan.tier);
    try {
      const response = await fetch('/api/stripe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planName: plan.name, tier: plan.tier })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to initialize checkout session.');
      }
    } catch (err: any) {
      setError(err.message || 'Subscription processing failed. Please try again later.');
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-black text-brand-900 mb-4 uppercase tracking-tight">Grow Your Practice</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Connect with premium development leads. Join Australia's most trusted consultant network.
          </p>
        </div>

        {error && (
          <div className="max-w-3xl mx-auto mb-10 bg-red-50 border border-red-100 p-4 rounded-xl flex items-center gap-3 text-red-700 animate-fade-in">
             <AlertCircle size={20} />
             <p className="text-sm font-bold">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {PRICING_PLANS.map((plan, index) => (
            <div 
              key={index}
              className={`
                relative bg-white rounded-3xl shadow-xl flex flex-col p-8 transition-transform duration-300
                ${plan.highlight ? 'ring-4 ring-brand-500 scale-105 z-10' : 'border border-gray-100'}
              `}
            >
              {plan.highlight && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-brand-500 text-white px-6 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">
                  Most Popular
                </div>
              )}
              
              <div className="mb-8">
                <h3 className="text-xl font-black text-gray-900 mb-1">{plan.name}</h3>
                <div className="flex items-baseline">
                  <span className="text-4xl font-black text-brand-900">{plan.price}</span>
                  {plan.price !== 'Free' && <span className="ml-1 text-gray-400 font-bold text-xs uppercase">/month</span>}
                </div>
              </div>

              <ul className="space-y-4 mb-10 flex-1">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start text-sm text-gray-600">
                    <Check className="flex-shrink-0 h-5 w-5 text-brand-500 mr-3" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => handleSubscribe(plan)}
                disabled={!!loadingTier}
                className={`
                w-full py-4 px-4 rounded-2xl font-black transition duration-200 uppercase tracking-widest text-xs flex items-center justify-center gap-2
                ${plan.highlight 
                  ? 'bg-brand-900 text-white hover:bg-black' 
                  : 'bg-gray-50 text-brand-900 hover:bg-brand-100 border border-brand-100'}
                ${loadingTier === plan.tier ? 'opacity-50 cursor-not-allowed' : ''}
              `}>
                {loadingTier === plan.tier ? <Loader2 className="animate-spin" size={16} /> : null}
                {plan.price === 'Free' ? 'Claim Free Listing' : 'Subscribe Now'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
